import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AttributePill, ActionChip } from './pills';
import { ValueSheet, ValueSheetOption } from './ValueSheet';
import { useWriteFeedback } from './useWriteFeedback';

export interface TicketDetail {
  id: string;
  number: string;
  subject: string;
  priority: string | null;
  status: string;
  resolvedAt: string | null;
  assigneeId: string | null;
  assigneeName: string | null;
  company: { id: string; name: string } | null;
  lead: { id: string; name: string | null } | null;
  conversations: Array<{ id: string; channel: string; status: string; startTime: string; endTime: string | null }>;
}

const PRIORITIES = ['low', 'medium', 'high', 'urgent'] as const;

// #50 (applied from day one, not retrofitted): on a real ticketId CHANGE, reset synchronously to an
// honest empty shell, never null — same converged fix as every other panel in this suite.
function emptyTicket(id: string): TicketDetail {
  return { id, number: '', subject: '', priority: null, status: '', resolvedAt: null, assigneeId: null, assigneeName: null, company: null, lead: null, conversations: [] };
}

/**
 * TicketDetailPanel — #62 next-phase Tickets depth spec (UXeng §2/§6), DISTINCT entity from
 * Conversation (§6.1). ONE surface for create AND detail/edit, same shape as every panel in this
 * suite. Two create entry points: the header (`ticketId === 'new'`, manual company/contact link) and
 * T1's one-tap-from-conversation (`prefill` prop — context already resolved, no re-picking a link).
 *
 * Status is action-driven (Resolve/Undo — a ticket has exactly two states, matching Task's Complete/
 * Undo, not Deal's outcome pipeline). T3: resolving OFFERS to close the linked conversation(s) via a
 * SEPARATE, explicit follow-up action — resolveTicket() itself never touches Conversation (T5/§6.2).
 */
export const TicketDetailPanel: React.FC<{
  ticketId: string | 'new';
  sheetShape: 'sheet' | 'popover';
  prefill?: { conversationId: string; companyId: string | null; leadId: string | null } | null;
  onClose: () => void;
  onCreated: (ticket: TicketDetail) => void;
  onUpdated: (ticket: TicketDetail) => void;
}> = ({ ticketId, sheetShape, prefill, onClose, onCreated, onUpdated }) => {
  const { t } = useTranslation('common');
  const [ticket, setTicket] = useState<TicketDetail | null>(null);
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'error'>(ticketId === 'new' ? 'ready' : 'loading');
  const [companies, setCompanies] = useState<{ id: string; name: string }[]>([]);
  const [contacts, setContacts] = useState<{ id: string; name: string | null }[]>([]);
  const [members, setMembers] = useState<{ id: string; name: string | null }[]>([]);
  const [createSubject, setCreateSubject] = useState('');
  // A3 (design §4.3): failed writes are VISIBLE; a landed-but-unconfirmed write is a THIRD outcome
  // with its own copy and a re-check affordance, never a retry invitation.
  const feedback = useWriteFeedback();
  const [createError, setCreateError] = useState(false);
  const [pendingCreatedId, setPendingCreatedId] = useState<string | null>(null);
  const [linkKind, setLinkKind] = useState<'company' | 'lead'>('company');
  const [linkId, setLinkId] = useState<string | null>(null);
  const [editingField, setEditingField] = useState<'subject' | null>(null);
  const [fieldDraft, setFieldDraft] = useState('');
  const [editingPicker, setEditingPicker] = useState<{ field: 'priority' | 'assignee'; anchor: HTMLElement } | null>(null);
  const [closeOffer, setCloseOffer] = useState<{ conversationId: string } | null>(null);
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    if (ticketId === 'new') return;
    // 🔴 root fix (cmdr/DevOps, 2026-08-25 — same defect found+fixed in InvoiceDetailPanel, applied
    // here on the identical pattern): right after create, the parent flips ticketId from 'new' to the
    // real id on the SAME panel instance — this effect re-fires, but `ticket` here already holds fresh
    // data for that exact id. Re-fetching anyway forces a real `loadState:'loading'` window, and every
    // inline-edit click handler gates on `!isLoading` — a click landing in that window is SILENTLY
    // SWALLOWED. Skip the redundant round-trip when we already have this exact record.
    if (ticket && ticket.id === ticketId) { hasLoadedRef.current = true; return; }
    setLoadState('loading');
    if (hasLoadedRef.current) setTicket(emptyTicket(ticketId));
    fetch(`/api/tickets/${ticketId}`).then((r) => r.ok ? r.json() : Promise.reject(r.status)).then((d: TicketDetail) => {
      setTicket(d); setLoadState('ready'); hasLoadedRef.current = true;
    }).catch(() => setLoadState('error'));
  }, [ticketId]);

  useEffect(() => {
    fetch('/api/companies').then((r) => r.ok ? r.json() : []).then(setCompanies).catch(() => {});
    fetch('/api/customers?kind=contact').then((r) => r.ok ? r.json() : []).then((rows) => setContacts((rows as any[]).map((c) => ({ id: c.id, name: c.name })))).catch(() => {});
    fetch('/api/tasks/assignees').then((r) => r.ok ? r.json() : []).then(setMembers).catch(() => {});
  }, []);

  const submitCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const subject = createSubject.trim();
    if (!subject) return;
    const body: Record<string, unknown> = { subject };
    if (prefill) {
      body.companyId = prefill.companyId; body.leadId = prefill.leadId; body.conversationId = prefill.conversationId;
      if (!prefill.companyId && !prefill.leadId) return; // context resolved to nothing linkable — refuse rather than create an orphan
    } else {
      if (!linkId) return;
      if (linkKind === 'company') body.companyId = linkId; else body.leadId = linkId;
    }
    const res = await fetch('/api/tickets', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }).catch(() => null);
    // D-K4: a failed create is a SAVE error, not a load error — the form (and the typed subject)
    // stays on screen, with a distinct message. ContactCreateForm is the shipped precedent.
    if (!res?.ok) { setCreateError(true); return; }
    setCreateError(false); // a successful retry clears the stale error
    const { ticketId: newId } = await res.json();
    // The write LANDED; only the confirming GET can fail from here. Never re-offer the form — a
    // resubmit would create a second ticket. The record becomes re-CHECKABLE instead.
    const ok = await confirmCreated(newId);
    if (!ok) setPendingCreatedId(newId);
  };

  const confirmCreated = async (newId: string): Promise<boolean> => {
    const detailRes = await fetch(`/api/tickets/${newId}`).catch(() => null);
    if (!detailRes?.ok) return false;
    const detail: TicketDetail = await detailRes.json();
    setPendingCreatedId(null);
    setTicket(detail);
    onCreated(detail);
    return true;
  };

  const patch = async (data: Partial<{ subject: string; priority: string | null }>): Promise<boolean> => {
    if (!ticket) return false;
    const res = await fetch(`/api/tickets/${ticket.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }).catch(() => null);
    if (!res?.ok) { feedback.fail(t('writeStates.saveFailed')); return false; }
    const updated: TicketDetail = await res.json();
    setTicket(updated);
    onUpdated(updated);
    return true;
  };

  /** Confirming refetch. Returns false instead of throwing — the caller decides whether a failed
   *  confirmation is the unconfirmed outcome (write landed) or noise. */
  const refresh = async (): Promise<boolean> => {
    if (!ticket) return false;
    const r = await fetch(`/api/tickets/${ticket.id}`).catch(() => null);
    if (!r?.ok) return false;
    const refreshed: TicketDetail = await r.json();
    setTicket(refreshed);
    onUpdated(refreshed);
    return true;
  };
  /** write -> confirm, separating the three outcomes: refused (visible failure, nothing changed),
   *  landed+confirmed (clear any stale error), landed+unconfirmed (re-check, never re-submit). */
  const runVerb = async (write: () => Promise<Response>): Promise<boolean> => {
    const res = await write().catch(() => null);
    if (!res?.ok) { feedback.fail(t('writeStates.actionFailed')); return false; }
    if (await refresh()) { feedback.clear(); return true; }
    feedback.unconfirmed(t('writeStates.confirmFailed'), t('writeStates.recheck'), () => { void refresh().then((ok) => { if (ok) feedback.clear(); }); });
    return true;
  };

  const reassign = async (assigneeId: string | null) => {
    if (!ticket) return;
    await runVerb(() => fetch(`/api/tickets/${ticket.id}/assignee`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ assigneeId }) }));
  };

  // T3: resolve is ONE action; closing the conversation is a SEPARATE, explicit follow-up the agent
  // must accept — never triggered automatically by resolve itself.
  const resolve = async () => {
    if (!ticket) return;
    const landed = await runVerb(() => fetch(`/api/tickets/${ticket.id}/resolve`, { method: 'POST' }));
    if (!landed) return; // a REFUSED resolve must not offer to close the conversation
    const openConv = ticket.conversations.find((c) => !c.endTime);
    if (openConv) setCloseOffer({ conversationId: openConv.id });
  };
  const reopen = async () => { if (!ticket) return; await runVerb(() => fetch(`/api/tickets/${ticket.id}/reopen`, { method: 'POST' })); };
  const closeLinkedConversation = async () => {
    if (!closeOffer) return;
    const offer = closeOffer;
    const landed = await runVerb(() => fetch(`/api/conversations/${offer.conversationId}/close`, { method: 'POST' }));
    if (landed) setCloseOffer(null); // a refused close keeps the offer on screen — nothing happened
  };

  const priorityOptions: ValueSheetOption[] = PRIORITIES.map((p) => ({ value: p, label: t(`ticketsGrid.priority.${p}`) }));
  const memberOptions: ValueSheetOption[] = members.map((m) => ({ value: m.id, label: m.name ?? '—' }));
  const linkOptions = linkKind === 'company' ? companies.map((c) => ({ value: c.id, label: c.name })) : contacts.map((c) => ({ value: c.id, label: c.name ?? '—' }));

  const startEdit = (field: 'subject', current: string) => { setFieldDraft(current); setEditingField(field); };
  const saveEdit = () => {
    if (!editingField || !ticket) return;
    const f = editingField;
    setEditingField(null);
    if (fieldDraft.trim() && fieldDraft !== ticket.subject) {
      // D-C5 family: a failed edit re-opens the field WITH the draft — the typed text must never
      // vanish and read as saved. (fieldDraft is untouched by the close, so reopening restores it.)
      void patch({ subject: fieldDraft.trim() }).then((ok) => { if (!ok) setEditingField(f); });
    }
  };

  const isLoading = loadState === 'loading';

  return (
    <div className="flex flex-col h-full w-full overflow-y-auto bg-canvas" data-testid="ticket-detail-panel">
      <button data-testid="ticket-detail-back" className="lg:hidden self-start flex items-center gap-1 px-4 py-3 min-h-[44px] text-sm text-primary font-semibold"
        onClick={onClose}>&larr; {t('taskBoard.back')}</button>

      <div className="flex flex-col gap-4 p-4 pb-16 lg:pb-4">
        {ticketId === 'new' && !ticket && !pendingCreatedId && (
          <form data-testid="ticket-create-form" onSubmit={submitCreate} className="rounded-xl border border-border-neutral bg-surface p-4 flex flex-col gap-3">
            <input autoFocus data-testid="ticket-create-subject" value={createSubject} onChange={(e) => setCreateSubject(e.target.value)}
              placeholder={t('ticketsGrid.columns.subject')} className="h-10 px-3 rounded-lg border border-border-neutral text-sm font-semibold" dir="auto" />
            {prefill ? (
              <div className="text-xs text-text-muted" data-testid="ticket-create-prefill-note">{t('ticketsGrid.linkedToConversation')}</div>
            ) : (
              <>
                <div className="flex gap-2">
                  <button type="button" data-testid="ticket-link-kind-company" onClick={() => { setLinkKind('company'); setLinkId(null); }}
                    className={`h-9 px-3 rounded-lg text-xs font-semibold ${linkKind === 'company' ? 'bg-primary text-white' : 'bg-canvas text-text-muted'}`}>{t('accountsGrid.columns.name')}</button>
                  <button type="button" data-testid="ticket-link-kind-contact" onClick={() => { setLinkKind('lead'); setLinkId(null); }}
                    className={`h-9 px-3 rounded-lg text-xs font-semibold ${linkKind === 'lead' ? 'bg-primary text-white' : 'bg-canvas text-text-muted'}`}>{t('contactsGrid.columns.name')}</button>
                </div>
                <select data-testid="ticket-create-link" value={linkId ?? ''} onChange={(e) => setLinkId(e.target.value || null)}
                  className="h-10 px-3 rounded-lg border border-border-neutral text-sm">
                  <option value="">{t('pipelineGrid.selectLink')}</option>
                  {linkOptions.map((o) => <option key={o.value ?? ''} value={o.value ?? ''}>{o.label}</option>)}
                </select>
              </>
            )}
            {createError && <div className="text-xs text-opt-rose" data-testid="ticket-create-error">{t('writeStates.createFailed')}</div>}
            <button type="submit" disabled={!createSubject.trim() || (!prefill && !linkId)} data-testid="ticket-create-submit"
              className="h-10 px-4 rounded-lg bg-primary text-white text-sm font-semibold disabled:opacity-50 self-start">
              {t('ticketsGrid.create')}
            </button>
          </form>
        )}

        {pendingCreatedId && (
          <div className="p-6 text-center text-sm text-text-muted flex flex-col items-center gap-3" data-testid="ticket-create-unconfirmed">
            {t('writeStates.confirmFailed')}
            <button type="button" data-testid="ticket-create-unconfirmed-retry" onClick={() => void confirmCreated(pendingCreatedId)}
              className="min-h-[44px] px-4 rounded-lg border border-border-neutral text-sm font-semibold text-text-strong hover:bg-canvas">
              {t('writeStates.recheck')}
            </button>
          </div>
        )}

        {ticketId !== 'new' && loadState === 'loading' && !ticket && <div className="p-6 text-center text-sm text-text-muted">…</div>}
        {loadState === 'error' && !ticket && <div className="p-6 text-center text-sm text-text-muted" data-testid="ticket-detail-error">{t('customers.detail.loadError')}</div>}

        {ticket && (
          <div data-testid="ticket-detail-content" aria-busy={isLoading}>
            {isLoading && (
              <div data-testid="ticket-detail-revalidating" className="text-xs text-text-muted text-center py-1" aria-live="polite">{t('customers.detail.loading')}</div>
            )}
            {/* identity */}
            <div className="rounded-xl border border-border-neutral bg-surface p-4">
              {editingField === 'subject' ? (
                <input autoFocus data-testid="ticket-subject-input" value={fieldDraft} onChange={(e) => setFieldDraft(e.target.value)}
                  onBlur={saveEdit} onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') setEditingField(null); }}
                  className="w-full h-9 px-2 -mx-2 rounded-lg border border-primary text-lg font-bold" dir="auto" />
              ) : (
                <h2 data-testid="ticket-subject" className={`text-lg font-bold text-text-strong truncate cursor-text ${ticket.status === 'resolved' ? 'opacity-70' : ''}`}
                  onClick={() => { if (!isLoading) startEdit('subject', ticket.subject); }}>{ticket.subject || '—'}</h2>
              )}
              <div className="text-xs text-text-muted mt-1">{ticket.number || '—'}</div>
            </div>

            {/* state — status (read-only), priority + assignee (AttributePill pickers) */}
            <div className="rounded-xl border border-border-neutral bg-surface p-4 flex flex-col gap-3">
              <div className="flex items-center gap-2 flex-wrap">
                <AttributePill value={ticket.status ? t(`ticketsGrid.status.${ticket.status}`) : null} label={t('ticketsGrid.columns.status')} />
                <AttributePill value={ticket.priority ? t(`ticketsGrid.priority.${ticket.priority}`) : null} label={t('ticketsGrid.columns.priority')}
                  emptyText={t('customers.detail.setField', { field: t('ticketsGrid.columns.priority') })}
                  onOpen={(a) => { if (!isLoading) setEditingPicker({ field: 'priority', anchor: a }); }} />
                <AttributePill value={ticket.assigneeName} label={t('ticketsGrid.columns.assignee')}
                  emptyText={t('customers.detail.setField', { field: t('ticketsGrid.columns.assignee') })}
                  onOpen={(a) => { if (!isLoading) setEditingPicker({ field: 'assignee', anchor: a }); }} />
              </div>

              {ticket.status === 'open' && ticket.number && <ActionChip testId="ticket-resolve" label={t('ticketsGrid.resolve')} onAct={resolve} disabled={isLoading} />}
              {ticket.status === 'resolved' && <ActionChip testId="ticket-reopen" label={t('ticketsGrid.reopen')} onAct={reopen} disabled={isLoading} />}

              {closeOffer && (
                <div className="rounded-lg border border-border-neutral bg-canvas p-3 flex items-center justify-between gap-2" data-testid="ticket-close-conversation-offer">
                  <span className="text-xs text-text-muted">{t('ticketsGrid.closeConversationOffer')}</span>
                  <div className="flex items-center gap-2 shrink-0">
                    <button type="button" data-testid="ticket-close-conversation-yes" onClick={closeLinkedConversation}
                      className="h-8 px-3 rounded-lg bg-primary text-white text-xs font-semibold">{t('pipelineGrid.confirm')}</button>
                    <button type="button" data-testid="ticket-close-conversation-no" onClick={() => setCloseOffer(null)}
                      className="h-8 px-3 rounded-lg text-xs font-semibold text-text-muted">{t('console.dismiss', 'Dismiss')}</button>
                  </div>
                </div>
              )}
            </div>

            {/* related — account/contact + linked conversations (T2: real data, not memory) */}
            <div className="rounded-xl border border-border-neutral bg-surface p-4">
              <div className="text-[11px] font-semibold text-text-muted uppercase tracking-wide mb-1">{t('accountsGrid.columns.name')}</div>
              <div data-testid="ticket-company" className="text-sm text-text-strong">{ticket.company?.name ?? ticket.lead?.name ?? '—'}</div>
            </div>

            <div className="rounded-xl border border-border-neutral bg-surface p-4">
              <div className="text-[11px] font-semibold text-text-muted uppercase tracking-wide mb-2">{t('ticketsGrid.conversations')}</div>
              {ticket.conversations.length > 0 ? (
                <div className="space-y-1.5" data-testid="ticket-conversations-list">
                  {ticket.conversations.map((c) => (
                    <div key={c.id} className="flex items-center justify-between text-sm gap-2">
                      <span className="text-text-strong">{c.channel}</span>
                      <span className="text-xs text-text-muted shrink-0">{c.endTime ? t('ticketsGrid.conversationClosed') : t('ticketsGrid.conversationOpen')}</span>
                    </div>
                  ))}
                </div>
              ) : <span className="text-text-muted italic text-sm">{t('ticketsGrid.noConversations')}</span>}
            </div>
          </div>
        )}
      </div>

      {editingPicker?.field === 'priority' && ticket && (
        <ValueSheet shape={sheetShape} title={`${t('ticketsGrid.columns.priority')} — ${ticket.subject}`} options={priorityOptions} current={ticket.priority}
          anchor={editingPicker.anchor} onSelect={(v) => { setEditingPicker(null); void patch({ priority: v }); }} onClose={() => setEditingPicker(null)} />
      )}
      {editingPicker?.field === 'assignee' && ticket && (
        <ValueSheet shape={sheetShape} title={`${t('ticketsGrid.columns.assignee')} — ${ticket.subject}`} options={memberOptions} current={ticket.assigneeId}
          anchor={editingPicker.anchor} onSelect={(v) => { setEditingPicker(null); void reassign(v); }} onClose={() => setEditingPicker(null)} />
      )}
      {feedback.node}
    </div>
  );
};
